import * as React from 'react';
import isNil from 'lodash/isNil';
import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';

import Spinner from 'components/spinner/Spinner';
import PageSection from 'components/page/page-section/PageSection';
import SubcampaignsTable from 'modules/subcampaigns/components/subcampaigns-table/SubcampaignsTable';
import CreateCollectionModal from 'modules/subcampaigns/components/create-collection-modal/CreateCollectionModal';
import CampaignParticipantsImportCollection from '../import-collection/CampaignParticipantsImportCollection';
import SubcampaignsEmptyMessage from '../subcampaigns-empty-message/SubcampaignsEmptyMessage';
import ShippingAddressesSection from '../shipping-adresses-section/ShippingAddressesSection';
import SubcampaignsTableHeader from '../subcampaigns-table-header/SubcampaignsTableHeader';
import BulkEditOfferModal from '../bulk/bulk-edit-offer-modal/BulkEditOfferModal';
import CampaignParticipantsImportCollectionModal from '../import-collection-modal/CampaignParticipantsImportCollectionModal';
import StatisticsSection from '../state-statistics-section/StatisticsSection';

import { getRevokedMetrics } from './helpers';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { danger } from 'redux-store/reducers/common-reducer';
import {
  clearShortlist,
  downloadParticipants,
  fetchParticipants,
  removeSubcampaigns,
  resendExpiredSubcampaignOffers,
  resetParticipants
} from 'modules/campaigns/reducers/campaignParticipants';
import {
  fetchVariablePayments,
  resetVariablePayments
} from 'modules/campaigns/reducers/variable-payments/variable-payments';
import {
  fetchCampaignDashboardStatistics,
  resetCampaignDashboardStatistics
} from 'modules/campaigns/reducers/dashboard/campaignDashboardStatistics';
import { fetchCampaignOverview } from 'modules/campaigns/reducers/campaignOverview';
import {
  fetchCampaignUISettingsShorlisted,
  resetCampaignUISettingsShorlisted
} from 'modules/campaigns/reducers/campaignUISettingsShorlisted';
import { fetchSubcampaign } from '../../reducers/subcampaign';

import { navigate } from 'libs/router';
import { askUserToConfirmRouteExitPromise } from 'libs/router/confirm-exit';

import { getShortlistedUISettings } from 'selectors/participants';

import { insertIf } from 'utils/list';
import { isInfluencerProfileApproved } from 'utils/profile';
import {
  PARTICIPANT_TYPE_AGREED,
  PARTICIPANT_TYPE_PENDING,
  PARTICIPANT_TYPE_REVOKED,
  PARTICIPANT_TYPE_SHORTLISTED,
  PARTICIPANT_TYPE_TO_STATE_MAP,
  sortParticipants
} from 'utils/campaignParticipants';
import { canCampaignBeChanged } from 'utils/campaign';
import { renderPrice } from 'utils/helper/price';
import { __t, applyTranslations } from 'utils/language';
import { abbrNum } from 'utils/helper/common';
import { logError } from 'utils/log';
import { getFormattedPercentageRate } from 'utils/formatters';
import { collectMetrics } from '../../utils/subcampaigns-metrics';

import type {
  Campaign,
  CampaignOverview,
  CampaignParticipantsColumnsVisibilityState,
  ContextMenuItem,
  MessengerNotifications,
  MetricsBarItem,
  Organization,
  ResponseTime,
  SortableTableSort,
  Subcampaign,
  SubcampaignOfferDetailsBuffer,
  SubcampaignSelectedIdsMap,
  VariablePayments
} from 'types';


applyTranslations(require('./subcampaigns-by-state.i18n.json'));

type OwnProps = {
  selectedParticipantsType: string,
  campaignId: number,
  subcampaign: Subcampaign | undefined | null,
  subcampaignOfferSortState?: string,
  isEditMode: boolean,
  onShowHelperPageStatus?: (a: boolean) => void
};

type StateProps = {
  campaign: Campaign,
  overview: CampaignOverview,
  isLoadingStatistics: boolean,
  messengerNotifications: MessengerNotifications,
  selectedIdsMap: SubcampaignSelectedIdsMap,
  currentOrganization: Organization,
  subcampaigns: Array<Subcampaign>,
  isLoading: boolean,
  variablePayments: VariablePayments,
  responseTimeByInfluencer: {
    [x: number]: ResponseTime
  },
  copiedOfferDetails: SubcampaignOfferDetailsBuffer | undefined | null,
  campaignUISettingsIsLoading: boolean,
  campaignUISettingsShorlisted: CampaignParticipantsColumnsVisibilityState
};

type DispatchProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  dispatchFetchSubcampaign: (subcampaignId: number) => Promise<void>,
  // eslint-disable-next-line react/no-unused-prop-types
  dispatchFetchParticipants: (campaignId: number, participantType: string) => Promise<void>, // false positive,
  dispatchDownloadParticipants: (campaignId: number, campaignName: string, participantType: string) => Promise<void>,
  dispatchResetParticipants: () => Promise<void>,
  dispatchFetchCampaignUISettingsShorlisted: (campaignId: number) => Promise<void>,
  dispatchResetCampaignUISettingsShorlisted: () => Promise<void>,
  dispatchFetchVariablePayments: (campaignId: number) => Promise<void>,
  dispatchResetVariablePayments: () => Promise<void>,
  dispatchFetchStatistics:
    (campaignId: number, selectedParticipantsTypeState: string, channel: string) => void,
  dispatchResetStatistics: () => void,
  dispatchRemoveSubcampaigns: (subcampaignIds: Array<number>) => Promise<void>,
  dispatchClearShortlist: (number: number) => Promise<void>,
  dispatchFetchOverview: (campaignId: number) => void,
  dispatchError: (message: string) => void,
  dispatchShowConfirmationModal: (campaignId: number) => void,
  dispatchAskUserToConfirmRouteExit: () => Promise<'next' | 'cancel'>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  channel: 'instagram' | 'tiktok',
  displayShippingAddresses: boolean,
  displayStatisticsPage: boolean,
  sortingOptions: SortableTableSort
};

class SubcampaignsByState extends React.PureComponent<Props, State> {
  state: State = {
    channel: 'instagram',
    displayShippingAddresses: false,
    displayStatisticsPage: false,
    sortingOptions: {
      sortingKey: 'followers',
      ascending: false
    }
  };

  componentDidMount() {
    const { campaignId, dispatchFetchCampaignUISettingsShorlisted, dispatchFetchVariablePayments } = this.props;

    this.fetchParticipants(this.props);

    dispatchFetchCampaignUISettingsShorlisted(campaignId);
    dispatchFetchVariablePayments(campaignId);
  }

  componentDidUpdate(prevProps: Props) {
    const {
      campaignId,
      selectedParticipantsType,
      onShowHelperPageStatus,
      dispatchFetchCampaignUISettingsShorlisted
    } = this.props;

    if (selectedParticipantsType !== prevProps.selectedParticipantsType) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        displayShippingAddresses: false,
        displayStatisticsPage: false
      });

      if (onShowHelperPageStatus) {
        onShowHelperPageStatus(false);
      }

      this.fetchParticipants(this.props);
    }

    if (campaignId !== prevProps.campaignId) {
      dispatchFetchCampaignUISettingsShorlisted(campaignId);
    }
  }

  componentWillUnmount() {
    const {
      dispatchResetCampaignUISettingsShorlisted,
      dispatchResetStatistics,
      dispatchResetParticipants,
      dispatchResetVariablePayments
    } = this.props;

    dispatchResetParticipants();
    dispatchResetStatistics();
    dispatchResetVariablePayments();
    dispatchResetCampaignUISettingsShorlisted();
  }

  handleChannelChange = (channel: 'tiktok' | 'instagram') => {
    this.setState({ channel }, this.fetchStatistic);
  };

  handlePasteBulkOffer = () => {
    const { campaign, selectedIdsMap, subcampaign } = this.props;
    const firstSelectedCampaign = Object.keys(selectedIdsMap)
      .find(key => selectedIdsMap[key]) || subcampaign?.id;
    if (firstSelectedCampaign) {
      navigate.to(`/campaigns/${campaign.id}/subcampaigns/${firstSelectedCampaign}/edit?new=1&bulk=create_offers&paste=active`);
    }
  };

  handleRemoveFromShortlist = () => {
    const { campaignId, selectedIdsMap, dispatchRemoveSubcampaigns, dispatchError } = this.props;
    const selectedIds = Object.keys(selectedIdsMap)
      .reduce<Array<number>>((result, key) => {
        if (selectedIdsMap[key]) {
          result.push(Number(key));
        }
        return result;
      }, []);

    dispatchRemoveSubcampaigns(selectedIds)
      .then(this.handleFullReFetch)
      .then(() => {
        navigate.to(`/campaigns/${campaignId}/participants/shortlisted`);
      })
      .catch((err) => {
        logError(err);
        dispatchError(__t('subcampaigns_by_state.error.remove_from_shortlist_failed'));
      });
  };

  handleClearShortlist = () => {
    const { campaignId, dispatchClearShortlist, dispatchError } = this.props;
    dispatchClearShortlist(campaignId)
      .then(this.handleFullReFetch)
      .then(() => {
        navigate.to(`/campaigns/${campaignId}/participants/shortlisted`);
      })
      .catch((err) => {
        logError(err);
        dispatchError(__t('subcampaigns_by_state.error.clear_shortlist_failed'));
      });
  };

  handleFullReFetch = () => {
    const { campaignId, dispatchFetchOverview } = this.props;

    this.fetchParticipants(this.props);
    dispatchFetchOverview(campaignId);
  };

  handleReloadSubcampaign = () => {
    const { subcampaign, dispatchFetchSubcampaign } = this.props;

    if (!isEmpty(subcampaign) && !isNil(subcampaign)) {
      dispatchFetchSubcampaign(subcampaign.id);
    }
  };

  handleShowAnalytics = async (): Promise<void> => {
    const { onShowHelperPageStatus, dispatchAskUserToConfirmRouteExit } = this.props;

    const status = await dispatchAskUserToConfirmRouteExit();

    if (status === 'cancel') {
      return;
    }

    this.setState({
      displayStatisticsPage: true
    });

    if (onShowHelperPageStatus) {
      onShowHelperPageStatus(true);
    }
  };

  handleShowAddresses = async (): Promise<void> => {
    const { onShowHelperPageStatus, dispatchAskUserToConfirmRouteExit } = this.props;

    const status = await dispatchAskUserToConfirmRouteExit();

    if (status === 'cancel') {
      return;
    }

    this.setState({
      displayShippingAddresses: true
    });

    if (onShowHelperPageStatus) {
      onShowHelperPageStatus(true);
    }
  };

  handleResendingExpiredOffers = (): void => {
    const { dispatchShowConfirmationModal, campaignId } = this.props;

    dispatchShowConfirmationModal(campaignId);
  };

  handleSortChanged = (sortingOptions: SortableTableSort) => this.setState({ sortingOptions });

  fetchStatistic = () => {
    const { campaignId, selectedParticipantsType, dispatchFetchStatistics } = this.props;
    const { channel } = this.state;

    dispatchFetchStatistics(campaignId, PARTICIPANT_TYPE_TO_STATE_MAP[selectedParticipantsType].state, channel);
  };

  fetchParticipants(nextProps: Props) {
    const {
      campaignId,
      selectedParticipantsType,
      dispatchFetchParticipants,
      dispatchFetchStatistics
    } = nextProps;
    const { channel } = this.state;

    dispatchFetchParticipants(campaignId, selectedParticipantsType);

    if (selectedParticipantsType !== PARTICIPANT_TYPE_REVOKED) {
      dispatchFetchStatistics(campaignId, PARTICIPANT_TYPE_TO_STATE_MAP[selectedParticipantsType].state, channel);
    }
  }

  downloadParticipants = () => {
    const {
      campaign,
      campaignId,
      selectedParticipantsType,
      dispatchDownloadParticipants
    } = this.props;

    dispatchDownloadParticipants(campaignId, campaign.name, selectedParticipantsType);
  };

  collectMetrics() {
    const { selectedParticipantsType, overview, subcampaigns, campaignUISettingsShorlisted } = this.props;

    const currentState = PARTICIPANT_TYPE_TO_STATE_MAP[selectedParticipantsType];

    if (!overview) {
      return [];
    }

    const metrics: Array<MetricsBarItem> = [{
      key: 'creators',
      name: __t('subcampaigns_by_state.header.creators_label'),
      value: overview[currentState.overviewName].creators || 0
    }];

    if (selectedParticipantsType !== PARTICIPANT_TYPE_REVOKED) {
      const subcampaignsMetrics = collectMetrics(subcampaigns, currentState.state);
      const skipUISettings = selectedParticipantsType !== PARTICIPANT_TYPE_SHORTLISTED;
      subcampaignsMetrics.forEach((metric) => {
        if (skipUISettings || campaignUISettingsShorlisted.channels[metric.channel]) {
          metrics.push({
            key: `followers-${metric.channel}`,
            name: __t(`subcampaigns_by_state.header.followers_by_channel_label.${metric.channel}`),
            value: abbrNum(metric.followers ?? 0),
            industryBenchmark: getFormattedPercentageRate(metric.avgEngagementRate),
            industryBenchmarkLabel: __t('subcampaigns_by_state.header.engagement_rate_label')
          });
        }
      });
    }

    if (selectedParticipantsType === PARTICIPANT_TYPE_PENDING) {
      metrics.push({
        key: 'pending-budget',
        name: __t('subcampaigns_by_state.header.pending_budget_label'),
        value: renderPrice(get(overview, 'pendingOverview.budget', 0) + get(overview, 'pendingOverview.variablePaymentsSum', 0))
      });
    }

    if (selectedParticipantsType === PARTICIPANT_TYPE_AGREED) {
      metrics.push({
        key: 'used-budget',
        name: __t('subcampaigns_by_state.header.used_budget_label'),
        value: renderPrice(get(overview, 'acceptedOverview.budget', 0) + get(overview, 'acceptedOverview.variablePaymentsSum', 0))
      });
    }

    if (selectedParticipantsType === PARTICIPANT_TYPE_REVOKED) {
      const revokedMetrics = getRevokedMetrics(subcampaigns);
      metrics.push(
        {
          key: 'canceled',
          name: __t('subcampaigns_by_state.header.canceled_metrics_label'),
          value: revokedMetrics.canceled
        },
        {
          key: 'declined',
          name: __t('subcampaigns_by_state.header.declined_metrics_label'),
          value: revokedMetrics.declined
        },
        {
          key: 'expired',
          name: __t('subcampaigns_by_state.header.expired_metrics_label'),
          value: revokedMetrics.expired
        }
      );
    }

    return metrics;
  }

  buildContextMenu(): Array<ContextMenuItem> {
    const {
      selectedParticipantsType,
      campaign,
      campaignId,
      copiedOfferDetails,
      selectedIdsMap,
      currentOrganization,
      overview,
      subcampaigns,
      isEditMode
    } = this.props;
    const { channel, sortingOptions } = this.state;
    const hasSelectedCampaign = Object.values(selectedIdsMap).some(value => value);
    const canEdit = canCampaignBeChanged(campaign);
    const isCA2CopiedOffer = copiedOfferDetails?.deliverables
      .some(deliverable => deliverable.contentApprovalV2) ?? false;
    const hasSameCampaignType = overview?.contentApprovalV2 === isCA2CopiedOffer;
    const canPasteOffer = canEdit && hasSelectedCampaign && copiedOfferDetails && hasSameCampaignType;
    const numOfExpiredOffers = (subcampaigns || []).filter(
      subcampaign => subcampaign.revokedBy === 'system' && subcampaign.state === 'revoked').length;
    const currentState = PARTICIPANT_TYPE_TO_STATE_MAP[selectedParticipantsType];
    const numOfSubcampaignWithApprovedInfluencers = (subcampaigns || []).filter(
      subcampaign => isInfluencerProfileApproved(subcampaign.creatorProfile)).length;

    const downloadAsExcel: ContextMenuItem = {
      type: 'link',
      name: 'download-as-excel',
      text: __t('subcampaigns_by_state.context_menu.download_as_excel'),
      onClick: this.downloadParticipants
    };

    switch (selectedParticipantsType) {
      case PARTICIPANT_TYPE_SHORTLISTED:
        return [
          ...insertIf(canEdit, {
            type: 'link',
            name: 'merged-offer-page-discover-shortlist',
            link: '/discovery/creators',
            openInNewTab: true,
            text: __t('subcampaigns_by_state.context_menu.discover_creators')
          }),
          ...insertIf(canEdit, {
            type: 'modal',
            name: 'merged-offer-page-import-collection',
            text: __t('subcampaigns_by_state.context_menu.import_collection'),
            meta: {
              campaignId,
              channel
            },
            extra: {
              component: CampaignParticipantsImportCollectionModal,
              modalProps: {
                showClose: false
              }
            }
          }),
          ...insertIf(canPasteOffer,
            {
              type: 'link',
              name: 'merged-offer-page-discover-shortlist',
              text: __t('subcampaigns_by_state.context_menu.paste_offer'),
              onClick: this.handlePasteBulkOffer
            }
          ),
          ...insertIf(canEdit && hasSelectedCampaign, {
            type: 'link',
            name: 'merged-offer-page-remove-selected-shortlist',
            text: __t('subcampaigns_by_state.context_menu.remove_from_shortlist'),
            onClick: this.handleRemoveFromShortlist
          }),
          ...insertIf(canEdit, {
            type: 'link',
            name: 'merged-offer-page-clear-shortlist',
            text: __t('subcampaigns_by_state.context_menu.clear_shortlist'),
            onClick: this.handleClearShortlist
          }),
          {
            type: 'modal',
            name: 'create-collection-shortlist',
            text: __t('subcampaigns_by_state.context_menu.create_from_shortlist'),
            meta: {
              campaignId,
              title: __t('subcampaigns_by_state.context_menu.create_from_shortlist'),
              organizationId: currentOrganization.id,
              offerState: PARTICIPANT_TYPE_TO_STATE_MAP[selectedParticipantsType].state
            },
            extra: {
              component: CreateCollectionModal
            }
          },
          downloadAsExcel
        ];
      case PARTICIPANT_TYPE_PENDING:
      case PARTICIPANT_TYPE_AGREED:
        return [
          ...insertIf(canEdit && !isEditMode && overview?.contentApprovalV2, {
            type: 'modal',
            name: `merged-offer-page-bulk-edit-${selectedParticipantsType}`,
            text: __t('subcampaigns_by_state.context_menu.bulk_edit_offers'),
            meta: {
              campaignId,
              defaultStates: [currentState.state],
              onBeforeModalClosed: this.handleReloadSubcampaign,
              sortingOptions
            },
            extra: {
              component: BulkEditOfferModal
            }
          }),
          ...insertIf(numOfSubcampaignWithApprovedInfluencers, {
            type: 'modal',
            name: 'create-collection',
            text: __t('subcampaigns_by_state.context_menu.create_collection'),
            meta: {
              campaignId,
              title: __t('subcampaigns_by_state.context_menu.create_collection'),
              organizationId: currentOrganization.id,
              offerState: PARTICIPANT_TYPE_TO_STATE_MAP[selectedParticipantsType].state
            },
            extra: {
              component: CreateCollectionModal
            }
          }),
          downloadAsExcel
        ];
      case PARTICIPANT_TYPE_REVOKED:
        return [
          ...insertIf(numOfExpiredOffers, {
            type: 'link',
            name: 'resent-expired-offers',
            text: __t('subcampaigns_by_state.context_menu.resend_expired_offers'),
            onClick: this.handleResendingExpiredOffers
          }),
          downloadAsExcel
        ];
      default:
        return [];
    }
  }

  render(): React.ReactNode {
    const {
      isLoading,
      campaign,
      overview,
      subcampaign,
      subcampaigns,
      isLoadingStatistics,
      campaignUISettingsIsLoading,
      messengerNotifications,
      variablePayments,
      selectedParticipantsType,
      responseTimeByInfluencer,
      subcampaignOfferSortState,
      onShowHelperPageStatus
    } = this.props;

    const { channel, displayShippingAddresses, displayStatisticsPage, sortingOptions } = this.state;

    const canEdit = (campaign && campaign.id) ? canCampaignBeChanged(campaign) : false;
    const currentState = PARTICIPANT_TYPE_TO_STATE_MAP[selectedParticipantsType];
    const subcampaignState = currentState.state;
    const isDraftUISettingsLoading = subcampaignState === 'draft' && campaignUISettingsIsLoading;

    if (isLoading || isDraftUISettingsLoading) {
      return (
        <section>
          <Spinner
            position="inline"
            size="small"
            color="light"
          />
        </section>
      );
    }

    const hasNoSubcampaigns = !subcampaigns || subcampaigns.length === 0;
    const isRevoked = selectedParticipantsType === PARTICIPANT_TYPE_REVOKED;

    if (!isRevoked && displayStatisticsPage) {
      return (
        <StatisticsSection
          campaignId={campaign.id}
          channel={channel}
          selectedParticipantsType={selectedParticipantsType}
          isLoadingStatistics={isLoadingStatistics}
          onChannelChange={this.handleChannelChange}
          onClose={() => {
            this.setState({
              displayStatisticsPage: false
            });

            if (onShowHelperPageStatus) {
              onShowHelperPageStatus(false);
            }
          }}
        />
      );
    }

    if (selectedParticipantsType === PARTICIPANT_TYPE_AGREED && displayShippingAddresses) {
      return (
        <ShippingAddressesSection
          campaignId={campaign.id}
          onClose={() => {
            this.setState({
              displayShippingAddresses: false
            });

            if (onShowHelperPageStatus) {
              onShowHelperPageStatus(false);
            }
          }}
        />
      );
    }

    const tableHeader = (
      <SubcampaignsTableHeader
        campaignId={campaign.id}
        isEmpty={hasNoSubcampaigns}
        metrics={this.collectMetrics()}
        participantsType={selectedParticipantsType}
        contextMenuItems={this.buildContextMenu()}
        onShowAnalytics={this.handleShowAnalytics}
        onShowAddresses={this.handleShowAddresses}
      />
    );

    if (canEdit && hasNoSubcampaigns && selectedParticipantsType === PARTICIPANT_TYPE_SHORTLISTED) {
      return (
        <section>
          {tableHeader}
          <PageSection style="volume" padding="none" margin="medium">
            <CampaignParticipantsImportCollection campaignId={campaign.id} />
          </PageSection>
        </section>
      );
    }

    if (hasNoSubcampaigns) {
      return (
        <section>
          {tableHeader}
          <PageSection style="none" padding="none" margin="medium">
            <SubcampaignsEmptyMessage
              canEdit={canEdit}
              subcampaignState={subcampaignState}
            />
          </PageSection>
        </section>
      );
    }
    return (
      <section>
        {tableHeader}
        <SubcampaignsTable
          isContentApprovalV2={overview?.contentApprovalV2}
          campaign={campaign}
          defaultSortingKey={sortingOptions.sortingKey}
          defaultSortingDirection={sortingOptions.ascending ? 'ascending' : 'descending'}
          subcampaign={subcampaign}
          subcampaignState={subcampaignState}
          subcampaigns={sortParticipants(subcampaigns)}
          messengerNotifications={messengerNotifications}
          variablePayments={variablePayments}
          responseTimeByInfluencer={responseTimeByInfluencer}
          overview={overview}
          subcampaignOfferSortState={subcampaignOfferSortState}
          onSortChanged={this.handleSortChanged}
        />
      </section>
    );
  }
}

const mapStateToProps = (
  {
    messengerNotifications,
    campaignBrief,
    campaignParticipants,
    campaignVariablePayments,
    influencerResponseTime,
    campaignOverview,
    campaignDashboardStatistics,
    campaignUISettingsShorlisted,
    offerDetailsBuffer,
    bulkOffer,
    organizations
  }
): StateProps => ({
  campaign: campaignBrief.brief,

  // fetched from overview component
  overview: campaignOverview.overview,

  isLoadingStatistics: campaignDashboardStatistics.isLoading.creator,
  messengerNotifications,
  selectedIdsMap: bulkOffer.selectedIdsMap,
  currentOrganization: organizations.currentOrganization,
  subcampaigns: campaignParticipants.list,
  isLoading: campaignParticipants.isLoading,
  variablePayments: campaignVariablePayments.variablePayments,
  responseTimeByInfluencer: influencerResponseTime.responseTimeData,
  copiedOfferDetails: offerDetailsBuffer.copiedOfferDetails,
  campaignUISettingsIsLoading: campaignUISettingsShorlisted.visibilityisLoading,
  campaignUISettingsShorlisted: getShortlistedUISettings(campaignUISettingsShorlisted.visibility)
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchSubcampaign: (subcampaignId: number) => dispatch(fetchSubcampaign(subcampaignId, { includeDetails: true })),

  dispatchFetchParticipants: (campaignId: number, type: string) => dispatch(
    fetchParticipants(campaignId, type, '', { includeDeliverablesOverview: true, includePayouts: true })),

  dispatchDownloadParticipants: (campaignId: number, campaignName: string, type: string) => dispatch(
    downloadParticipants(campaignId, campaignName, type, '', { includeDeliverablesOverview: true })
  ),

  dispatchResetParticipants: () => dispatch(resetParticipants()),
  dispatchRemoveSubcampaigns: (ids: Array<number>) => dispatch(removeSubcampaigns(ids)),
  dispatchClearShortlist: (campaignId: number) => dispatch(clearShortlist(campaignId)),
  dispatchFetchOverview: (campaignId: number) => dispatch(fetchCampaignOverview(campaignId)),
  dispatchError: (message: string) => dispatch(danger(message)),
  dispatchFetchCampaignUISettingsShorlisted: (campaignId: number) => dispatch(fetchCampaignUISettingsShorlisted(campaignId)),
  dispatchResetCampaignUISettingsShorlisted: () => dispatch(resetCampaignUISettingsShorlisted()),
  dispatchFetchVariablePayments: (campaignId: number) => dispatch(fetchVariablePayments(campaignId)),
  dispatchResetVariablePayments: (): Promise<void> => dispatch(resetVariablePayments()),

  dispatchFetchStatistics: (campaignId: number, filter: string, channel: string) =>
    dispatch(fetchCampaignDashboardStatistics('creator', campaignId, filter, channel)),

  dispatchResetStatistics: (): void => dispatch(resetCampaignDashboardStatistics('creator')),
  dispatchShowConfirmationModal: (campaignId: number): void => dispatch(showConfirmModal({
    title: __t('subcampaigns_by_state.resend_expired_offers.confirmation_modal_title'),
    message: __t('subcampaigns_by_state.resend_expired_offers.confirmation_modal_description'),
    confirmButtonLabel: __t('subcampaigns_by_state.resend_expired_offers.confirm_btn_label'),
    onConfirm: () => {
      dispatch(resendExpiredSubcampaignOffers(campaignId))
        .then(() => {
          dispatch(hideConfirmModal());

          navigate.to(`/campaigns/${campaignId}/participants/pending`);
        })
        .catch(() => {
          dispatch(danger(__t('subcampaigns_by_state.resend_expired_offers.error_message')));
        });
    },
    onCancel: () =>
      dispatch(hideConfirmModal())
  })),
  dispatchAskUserToConfirmRouteExit: () => askUserToConfirmRouteExitPromise(dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(SubcampaignsByState);
